// Componente de ruta protegida para la aplicación Amarsi Control Panel
// Controla el acceso a rutas basado en el estado de autenticación del usuario
// Mantiene la URL actual durante la verificación de autenticación al refrescar

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';

/**
 * Componente ProtectedRoute
 * 
 * Protege rutas que requieren autenticación
 * Redirige al login si el usuario no está autenticado
 * Mantiene la URL actual durante la verificación de autenticación
 * 
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Componentes hijos a renderizar si está autenticado
 * @param {string} props.redirectTo - Ruta de redirección si no está autenticado (default: "/auth/login")
 */
const ProtectedRoute = ({ 
  children, 
  redirectTo = '/auth/login' 
}) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Verificar si es una URL de retorno de Mercado Pago
  const isMercadoPagoReturn = location.pathname === '/mp-return' || 
                              location.pathname === '/suscripcion/return' ||
                              location.search.includes('preapproval_id=') ||
                              location.search.includes('payment_id=');

  // Muestra spinner mientras se verifica el estado de autenticación
  // Esto mantiene la URL actual y evita redirecciones prematuras
  if (loading) {
    // Si es retorno de Mercado Pago, mostrar un mensaje específico
    if (isMercadoPagoReturn) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Procesando tu suscripción...
            </h2>
            <p className="text-gray-600">
              Estamos verificando tu pago y actualizando tu cuenta.
            </p>
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                Por favor espera un momento mientras completamos el proceso.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Spinner normal para otras páginas
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950">
        <div className="text-center">
          <div className="text-neutral-50">
            <LoadingSpinner size={40}/>
          </div>
          <p className="text-neutral-50">Usa la funcion Explorar, <br />cambiá tu ubicación y descubre más personas 🌎</p>
        </div>
      </div>
    );
  }

  // Si no está autenticado, redirige al login preservando la ubicación actual
  // Esto permite volver a la página original después del login
  if (!isAuthenticated) {
    // Si es retorno de Mercado Pago y no está autenticado, mostrar opción de reintentar
    if (isMercadoPagoReturn) {
      console.log('🔄 Retorno de Mercado Pago sin autenticación, mostrando opciones...');
      
      // Mostrar mensaje de espera con botón de reintentar
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="w-16 h-16 border-4 border-yellow-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Verificando tu sesión...
            </h2>
            <p className="text-gray-600 mb-4">
              Estamos confirmando tu identidad para completar la suscripción.
            </p>
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800 mb-3">
                Si no se redirige automáticamente, puedes intentar:
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded transition-colors"
                >
                  Reintentar Verificación
                </button>
                <button
                  onClick={() => {
                    // Guardar la URL actual y redirigir al login
                    const returnUrl = window.location.pathname + window.location.search;
                    sessionStorage.setItem('mp_return_url', returnUrl);
                    window.location.href = '/auth/login';
                  }}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded transition-colors"
                >
                  Iniciar Sesión Manualmente
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Esto puede suceder si la sesión expiró o hay un problema con la autenticación.
            </p>
          </div>
        </div>
      );
    }

    // Guardamos la ruta actual en el estado para redirigir después del login
    const state = { from: location };
    return <Navigate to={redirectTo} state={state} replace />;
  }

  // Si está autenticado, renderiza los componentes hijos
  return <>{children}</>;
};

export default ProtectedRoute;
